import type React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ExpandableText } from "@/components/common/ExpandableText";
import { MediaPreview } from "@/components/common/MediaPreview";
import { SendingBadge } from "@/components/common/SendingBadge";
import { ActivityStatusBadge } from "@/features/study-group-stream/components/ActivityStatusBadge";
import { TranslationToggleButton } from "@/components/common/TranslationToggleButton";
import { UserAvatarWithPopup } from "@/components/common/UserAvatarWithPopup";
import { Card, CardContent } from "@/components/ui/card";
import { EventLinkPreviewProvider } from "@/contexts/EventLinkPreviewContext";
import { CommentEditForm } from "@/features/study-group-stream/components/CommentEditForm";
import type { EditableFileOps } from "@/components/common/hooks/useEditableAttachments";
import { CommentHeaderActions } from "@/features/study-group-stream/components/CommentHeaderActions";
import { InteractionFooter } from "@/features/study-group-stream/components/InteractionFooter";
import {
  getEffectiveIsSending,
  getIsAnyActionLoading,
} from "@/features/study-group-stream/utils/commentUiState";
import { useMentionInputNotice } from "@/hooks/useMentionInputNotice";
import { COMMENT_REACTION_OBJECT_MODEL } from "@/hooks/useCommentReactions";
import { useOriginalContentToggle } from "@/hooks/useOriginalContentToggle";
import { toast } from "@/hooks/useToast";
import type { Comment, ReactionType } from "@/lib/humhub-api/types";
import { cn } from "@/lib/utils";
import { convertTimezoneToLocal } from "@/lib/utils/dateUtils";

interface ReplyCardProps {
  author?: string;
  content?: string;
  timestamp?: string;
  avatarUrl?: string | null;
  currentReaction?: ReactionType | null;
  onReaction?: (
    commentId: number,
    reaction: ReactionType | null,
  ) => Promise<void>;
  isReacting?: boolean;
  comment?: Comment;
  isPending?: boolean;
  onReplyClick?: (commentId: number) => void;
  isSending?: boolean;
  onDeleteComment?: (commentId: number) => Promise<void>;
  onEditComment?: (
    commentId: number,
    newMessage: string,
    fileOps?: EditableFileOps,
  ) => Promise<void>;
  isDeleting?: boolean;
  isCurrentUserAuthor?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  showReplyButton?: boolean;
  currentUserId?: number | string;
  onSendMessage?: (userId: number) => void;
  isCommentActive?: boolean;
  isHighlighted?: boolean;
  isNew?: boolean;
  isMention?: boolean;
  disabled?: boolean;
}

export const ReplyCard: React.FC<ReplyCardProps> = ({
  author,
  content,
  timestamp,
  avatarUrl,
  currentReaction = null,
  onReaction,
  isReacting = false,
  comment,
  isPending = false,
  onReplyClick,
  isSending = false,
  onDeleteComment,
  onEditComment,
  isDeleting = false,
  isCurrentUserAuthor = false,
  canEdit,
  canDelete,
  showReplyButton = true,
  currentUserId,
  onSendMessage,
  isCommentActive = false,
  isHighlighted = false,
  isNew = false,
  isMention = false,
  disabled = false,
}) => {
  const { t } = useTranslation();

  const displayAuthor =
    author ||
    comment?.user?.display_name ||
    comment?.createdBy?.display_name ||
    "";
  const displayContent = content || comment?.message || "";
  const originalContent =
    typeof comment?.message_original === "string" &&
    comment.message_original.trim().length > 0
      ? comment.message_original
      : null;
  const editContent = originalContent ?? displayContent;
  const translationToggle = useOriginalContentToggle([
    { value: displayContent, original: originalContent },
  ]);
  const { showOriginal, canToggleOriginal, toggleOriginal, getVisibleContent } =
    translationToggle;
  const visibleContent = getVisibleContent(displayContent, originalContent);
  const displayTimestamp =
    timestamp || comment?.created_at || comment?.createdAt || "";
  const formattedTimestamp = (() => {
    if (!displayTimestamp) return "";
    const { date, time } = convertTimezoneToLocal(displayTimestamp, "UTC");
    if (!date || !time) return displayTimestamp;
    return `${date} ${time}`;
  })();
  const displayAvatarUrl =
    avatarUrl ||
    comment?.user?.image_url ||
    comment?.createdBy?.image_url ||
    "";
  const displayUserId = comment?.user?.id || comment?.createdBy?.id || 0;
  const displayCommentId = Number(comment?.id ?? 0);
  const hasDisplayCommentId =
    Number.isFinite(displayCommentId) && displayCommentId > 0;
  const displayReaction =
    currentReaction || comment?.reactions?.current_user_emotion || null;
  const effectiveIsSending = getEffectiveIsSending(comment, isSending);

  const [localReaction, setLocalReaction] = useState<ReactionType | null>(
    displayReaction,
  );
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedMessage, setEditedMessage] = useState(editContent);
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);
  const mentionNoticeResetKey = isEditMode
    ? `${displayCommentId}:${editContent}`
    : null;
  const hasTypedMentionInEdit = useMentionInputNotice(
    editedMessage,
    editContent,
    mentionNoticeResetKey,
  );

  useEffect(() => {
    setLocalReaction(displayReaction);
  }, [displayReaction]);

  useEffect(() => {
    setEditedMessage(editContent);
  }, [editContent]);

  const handleReactionSelect = async (reaction: ReactionType) => {
    if (!hasDisplayCommentId || !onReaction || disabled) return;

    const newReactionState = localReaction === reaction ? null : reaction;
    setLocalReaction(newReactionState);
    try {
      await onReaction(displayCommentId, newReactionState);
    } catch (error) {
      console.error("Reaction failed:", error);
      setLocalReaction(displayReaction);
    }
  };

  const handleReplyButtonClick = () => {
    if (onReplyClick && hasDisplayCommentId && !disabled) {
      onReplyClick(displayCommentId);
    }
  };

  const handleEditClick = (_commentId: number) => {
    setIsEditMode(true);
    setEditedMessage(editContent);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditedMessage(editContent);
  };

  const handleSubmitEdit = async (
    nextMessage: string,
    fileOps: EditableFileOps,
  ) => {
    const trimmedMessage = nextMessage.trim();
    setEditedMessage(nextMessage);
    const hasFileChanges =
      fileOps.filesToRemove.length > 0 ||
      fileOps.filesToReplace.length > 0 ||
      fileOps.filesToAdd.length > 0;
    if (
      !onEditComment ||
      (!hasFileChanges && trimmedMessage === editContent.trim())
    ) {
      setIsEditMode(false);
      return;
    }
    if (trimmedMessage === "") {
      toast({ description: t("common.messageCannotBeEmpty") });
      return;
    }
    if (!hasDisplayCommentId) return;
    setIsEditMode(false);
    setIsSubmittingEdit(true);
    try {
      await onEditComment(displayCommentId, trimmedMessage, fileOps);
    } catch (error) {
      console.error("Failed to edit reply:", error);
      toast({ variant: "error", description: t("common.editError") });
      setEditedMessage(editContent);
      setIsEditMode(true);
    } finally {
      setIsSubmittingEdit(false);
    }
  };

  const handleDeleteClick = async (commentId: number) => {
    if (!onDeleteComment) {
      console.error("❌ ReplyCard: onDeleteComment handler is missing!");
      return;
    }
    try {
      await onDeleteComment(commentId);
    } catch (error) {
      console.error("❌ ReplyCard: Failed to delete reply:", error);
    }
  };

  const isAnyActionLoading = getIsAnyActionLoading({
    isDeleting,
    isSubmittingEdit,
    isSending: effectiveIsSending,
  });

  return (
    <Card
      data-comment-id={displayCommentId}
      className={cn(
        // ml-8を削除して右端を他カードと揃える
        "group/comment flex w-full min-w-0 flex-col rounded-2xl border-none shadow-none",
        isHighlighted ? "bg-amber-50" : "bg-[#dfe5e7]",
        isHighlighted &&
          "transition-colors duration-300 motion-reduce:transition-none",
        isPending && "opacity-70",
        effectiveIsSending && "border-l-4 border-blue-500 bg-blue-50",
        isAnyActionLoading && "opacity-70",
      )}
    >
      <CardContent className="flex w-full min-w-0 flex-col gap-0 pb-2 pl-3 pt-3">
        <div className="flex w-full min-w-0 items-start gap-2">
          <UserAvatarWithPopup
            userId={displayUserId}
            userName={displayAuthor}
            avatarUrl={displayAvatarUrl}
            currentUserId={currentUserId}
            onSendMessage={onSendMessage}
            wrapperClassName="h-8 w-8 flex-shrink-0 cursor-pointer transition-opacity hover:opacity-80"
            avatarClassName="h-8 w-8 rounded-full"
            avatarFallbackClassName="rounded-full text-sm"
          />
          <div className="flex min-w-0 flex-1 flex-col">
            <div className="relative flex min-w-0 items-center pr-9">
              <div className="flex min-w-0 items-center gap-2">
                <span className="truncate text-sm font-semibold text-variable-collection-black-gray">
                  {displayAuthor}
                </span>
                <span className="text-xs font-medium text-rw-dark-gray">
                  {formattedTimestamp}
                </span>
                <ActivityStatusBadge isNew={isNew} isMention={isMention} />
                {effectiveIsSending && <SendingBadge />}
                {canToggleOriginal && !isEditMode && (
                  <TranslationToggleButton
                    showOriginal={showOriginal}
                    onToggle={toggleOriginal}
                    className="shrink-0 text-xs font-medium text-rw-main hover:underline"
                  />
                )}
              </div>
              {!disabled && (
                <div className="absolute right-0 top-1/2 z-30 -translate-y-1/2">
                  <CommentHeaderActions
                    commentId={displayCommentId ?? 0}
                    isCurrentUserAuthor={isCurrentUserAuthor}
                    canEdit={canEdit}
                    canDelete={canDelete}
                    isAnyActionLoading={isAnyActionLoading}
                    isDeleting={isDeleting}
                    onEditClick={handleEditClick}
                    onDeleteClick={handleDeleteClick}
                  />
                </div>
              )}
            </div>

            {isEditMode ? (
              <CommentEditForm
                value={editedMessage}
                isSubmitting={isSubmittingEdit}
                onChange={setEditedMessage}
                onSave={handleSubmitEdit}
                onCancel={handleCancelEdit}
                showMentionWarning={hasTypedMentionInEdit}
                existingFiles={comment?.files}
              />
            ) : (
              <div className="comment-body-offset min-w-0">
                <EventLinkPreviewProvider showOriginalContent={showOriginal}>
                  <ExpandableText content={visibleContent} />
                </EventLinkPreviewProvider>

                {/* Display file attachments if present */}
                {comment?.files && comment.files.length > 0 && (
                  <div className="mt-2">
                    <MediaPreview files={comment.files} />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <InteractionFooter
          localReaction={localReaction}
          isReacting={isReacting}
          isPending={isPending || effectiveIsSending}
          commentId={displayCommentId ?? 0}
          onReactionSelect={handleReactionSelect}
          showReplyButton={showReplyButton}
          onReplyClick={handleReplyButtonClick}
          isAnyActionLoading={isAnyActionLoading}
          reactionTotal={comment?.reactions?.total ?? 0}
          lastReactions={comment?.reactions?.last_reactions ?? []}
          reactionBreakdown={comment?.reactions?.by_emotion}
          reactionObjectModel={COMMENT_REACTION_OBJECT_MODEL}
          reactionObjectId={displayCommentId ?? 0}
          isCommentActive={isCommentActive}
          disabled={disabled}
          dividerClassName="border-[#c3ccd1]"
          reactionInactiveIconOpacityClass="opacity-85"
        />
      </CardContent>
    </Card>
  );
};
